/**
 * ============================================================================
 * POPUP MANAGER
 * ============================================================================
 *
 * Manages theme pop-ups with cookie-based tracking and frequency control.
 * Integrates with the existing ModalManager for consistent behavior.
 *
 * ============================================================================
 */

class PopupManager {
  constructor() {
    this.settings = null;
    this.modalManager = null;
    this.isInitialized = false;

    this.init();
  }

  /**
   * Initialize popup manager
   */
  init() {
    // Check if settings are already available
    if (window.CustomThemeSettings) {
      this.settings = window.CustomThemeSettings;

      this.checkModalManager();
    }

    // Wait for theme settings and modal manager
    document.addEventListener('custom-theme:settings-loaded', (e) => {
      this.settings = e.detail;

      this.checkModalManager();
    });

    // Listen for app ready event to get modal manager
    document.addEventListener('app:ready', () => {

      this.checkModalManager();
    });


  }

  /**
   * Check if modal manager is available and initialize popup
   */
  checkModalManager() {
    if (this.isInitialized) {
      return;
    }

    // Get modal manager from global app
    if (window.DeSightApp && window.DeSightApp.modules && window.DeSightApp.modules.get('modal')) {
      this.modalManager = window.DeSightApp.modules.get('modal');
      this.initializePopup();
    }
  }

  /**
   * Initialize popup based on settings
   */
  initializePopup() {
    if (this.isInitialized) {
      return;
    }

    // Check if popup is enabled in theme settings
    if (!this.settings?.popup?.enabled) {
      return;
    }

    this.isInitialized = true;

    // Check if popup should be shown
    const shouldShow = this.shouldShowPopup();

    if (shouldShow) {
      this.schedulePopup();
    }
  }

  /**
   * Check if popup should be shown based on frequency and dates
   */
  shouldShowPopup() {
    // Check date range
    if (!this.isWithinDateRange()) {
      return false;
    }

    // Check frequency/cookie
    if (this.hasValidCookie()) {
      return false;
    }

    return true;
  }

  /**
   * Check if current date is within popup date range
   */
  isWithinDateRange() {
    const now = new Date();
    const startDate = this.settings.popup.startDate;
    const endDate = this.settings.popup.endDate;

    // Check start date
    if (startDate) {
      const start = new Date(startDate);
      if (now < start) return false;
    }

    // Check end date
    if (endDate) {
      const end = new Date(endDate);
      if (now > end) return false;
    }

    return true;
  }

  /**
   * Check if popup cookie exists and is valid
   */
  hasValidCookie() {
    const cookieName = this.settings.popup.cookieName || 'popup_shown';
    const cookie = this.getCookie(cookieName);

    if (!cookie) {
      return false;
    }

    try {
      const frequency = this.settings.popup.frequency || 'session';
      const cookieData = JSON.parse(cookie);
      const lastShown = new Date(cookieData.timestamp);
      const now = new Date();

      switch (frequency) {
        case 'session':
          // Cookie exists = already shown this session
          return true;

        case 'daily':
          const daysDiff = Math.floor((now - lastShown) / (1000 * 60 * 60 * 24));
          return daysDiff < 1;

        case 'weekly':
          const weeksDiff = Math.floor((now - lastShown) / (1000 * 60 * 60 * 24 * 7));
          return weeksDiff < 1;

        case 'monthly':
          const monthsDiff = Math.floor((now - lastShown) / (1000 * 60 * 60 * 24 * 30));
          return monthsDiff < 1;

        default:
          return false;
      }
    } catch (error) {
      return false;
    }
  }

  /**
   * Schedule popup to show after delay
   */
  schedulePopup() {
    const delay = (this.settings.popup.delay || 2) * 1000;

    setTimeout(() => {
      this.showPopup();
    }, delay);
  }

  /**
   * Show the popup
   */
  showPopup() {
    // Create popup HTML if it doesn't exist
    if (!document.getElementById('theme-popup')) {
      this.createPopupHTML();
    }

    // Use modal manager to open popup
    if (this.modalManager) {
      // For auto-triggered popups, set trigger element to body for proper focus restoration
      this.modalManager.triggerElement = document.body;
      this.modalManager.openModal('theme-popup');
      this.setCookie();
    }
  }

  /**
   * Create popup HTML structure
   */
  createPopupHTML() {
    const popup = document.createElement('div');
    popup.id = 'theme-popup';
    popup.className = `modal popup-modal popup-size-${this.settings.popup.size || 'medium'}`;
    popup.setAttribute('aria-hidden', 'true');
    popup.setAttribute('data-backdrop-static', this.settings.popup.backdropClose ? 'false' : 'true');

    const colorScheme = this.settings.popup.colorScheme || 'scheme-1';

    popup.innerHTML = `
      <div class="modal-dialog ${colorScheme}">
        <div class="modal-content">
          <div class="modal-header">
            <h2 class="modal-title">${this.settings.popup.title || 'Willkommen!'}</h2>
            <button type="button" class="modal-close" data-modal-close="theme-popup" aria-label="Schließen">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="modal-body">
            ${this.settings.popup.image ?
              `<div class="popup-image">
                <img src="${this.settings.popup.image}" alt="${this.settings.popup.title || 'Pop-up Bild'}" loading="lazy">
              </div>` : ''
            }
            <div class="popup-content">
              ${this.settings.popup.content || '<p>Willkommen in unserem Shop!</p>'}
            </div>
          </div>
          <div class="modal-footer">
            ${this.settings.popup.buttonText && this.settings.popup.buttonLink && this.settings.popup.buttonLink !== '#' ?
              `<a href="${this.settings.popup.buttonLink}" class="btn btn-primary popup-cta">
                ${this.settings.popup.buttonText}
              </a>` : ''
            }
            <button type="button" class="btn btn-secondary" data-modal-close="theme-popup">
              Schließen
            </button>
          </div>
        </div>
      </div>
    `;

    // Append to body
    document.body.appendChild(popup);

    // Add additional event listeners for close functionality
    const closeButtons = popup.querySelectorAll('[data-modal-close]');
    closeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (this.modalManager) {
          this.modalManager.closeModal('theme-popup');
        }
      });
    });


  }

  /**
   * Set cookie to track popup display
   */
  setCookie() {
    const cookieName = this.settings.popup.cookieName || 'popup_shown';
    const duration = parseInt(this.settings.popup.cookieDuration || '7');
    const frequency = this.settings.popup.frequency || 'session';

    const cookieData = {
      timestamp: new Date().toISOString(),
      frequency: frequency
    };

    // Set cookie duration based on frequency
    let expires = '';
    if (frequency !== 'session') {
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + duration);
      expires = `; expires=${expiryDate.toUTCString()}`;
    }

    document.cookie = `${cookieName}=${JSON.stringify(cookieData)}${expires}; path=/; SameSite=Lax`;


  }

  /**
   * Get cookie value
   */
  getCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }

    return null;
  }

  /**
   * Manually close popup (for external use)
   */
  closePopup() {
    if (this.modalManager) {
      this.modalManager.closeModal('theme-popup');
    }

    // Additional cleanup: ensure popup is completely removed
    setTimeout(() => {
      const popup = document.getElementById('theme-popup');
      if (popup && popup.parentNode) {
        popup.parentNode.removeChild(popup);
      }
    }, 400);
  }

  /**
   * Reset popup cookie (for testing)
   */
  resetCookie() {
    const cookieName = this.settings?.popup?.cookieName || 'popup_shown';
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;

  }
}

// Export for use in main app
if (typeof module !== 'undefined' && module.exports) {
  module.exports = PopupManager;
} else {
  window.PopupManager = PopupManager;
}
